import React, { useRef, useEffect, useMemo } from "react";
import { attemptFullscreenToggle } from "../../../../utilities";
import { Sprite, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDispatch, useSelector } from "react-redux";
import { SOUND_NAME } from "../../../../sounds";
import { getNewUITexture } from "../../../../assets";
import ButtonShadow from "./ButtonShadow";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import {
  setAutoplaySpeed,
  setAutoplaySpeedNoSave,
  setPendingAutoplaySpeed,
  closeMenu,
  closeSymbolPopup,
} from "../../../../State/actions";
import {
  selectAutoPlaySpeed,
  selectPendingAutoPlaySpeed,
  selectIsSpinning,
  selectHoldAndWinActive,
  selectFeatureSpinActive,
  selectWindupComplete,
  selectShowHoldAndWinCollector,
  selectPotCollectionActive,
  selectAnyFeatureTriggeredOrActive,
  selectReplaySessionActive,
} from "../../../../State/selectors";
import { useReelControl } from "../../../../Contexts/ReelControlContext";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import store from "../../../../State/Store/store";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";
import { isTurboLike, isSuperTurbo } from "../../../../utils/speedHelpers";
// Cleopatra's Hold & Win round (its own context — not the legacy one below).
import { useHoldAndWinControl as useRespinControl } from "../../../../Contexts/HoldAndWinControlContext";
import { useFreeSpinBasicControl } from "../../../../Contexts/FreeSpinBasicControlContext";

// Rate limiting for button clicks
let lastClickTime = 0;
const MIN_CLICK_GAP = 100; // Minimum safe gap to prevent state corruption while maintaining responsiveness

// EVENT-LEVEL THROTTLE: Blocks click handler execution at entry point
// Prevents performance degradation from rapid clicking (50+ clicks/sec)
let clickThrottleActive = false;
let clickThrottleTimeout = null;

// Turbo button configuration - matches UI button dimensions
const TURBO_BUTTON_CONFIG = {
  BASE_WIDTH: 80,
  BASE_HEIGHT: 80,
  // An iPad upright (layout TABLET_PORTRAIT on the MOBILE device, 2026-10-29):
  // a touch bigger and a bit further from the spin button, on their ruler.
  LAYOUT_CONFIGS: {
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.1 },
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.09 }, // a notch closer
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.07 }, // turbo sat further out than autoplay
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.07, gapAboveToolbar: 0.14 }, // a tiny bit up
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.07, gapAboveToolbar: 0.16 }, // a bit more up
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.07, gapAboveToolbar: 0.19 }, // more
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.07, gapAboveToolbar: 0.21 }, // 2 notches more
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.07, gapAboveToolbar: 0.23 }, // a bit more
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.07, gapAboveToolbar: 0.25 }, // 2 more
    // TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.07, gapAboveToolbar: 0.27 },
    TABLET_PORTRAIT: { deviceMultiplier: 2.1, sideOffset: 2.07, gapAboveToolbar: 0.29 }, // matched on their ruler (2026-10-29)
  },
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.03,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.575,
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.04,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.45,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.7,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

const ButtonTurbo = ({ toolbar }) => {
  const dispatch = useDispatch();
  const {
    getResponsiveConfig,
    device,
    orientation,
    dimensions,
    isMobileSmall,
  } = useDimensions();
  const { handleManualStop } = useReelControl();

  // Hold and Win hooks - only used when feature is enabled and context is available
  // These hooks are conditionally imported to avoid requiring providers that may not exist
  let handleHoldAndWinManualStop = null;
  let triggerCollectionSkip = null;
  if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED) {
    try {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const {
        useHoldAndWinControl,
      } = require("../../../../Contexts/HoldAndWinControlContext");
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const holdAndWinControl = useHoldAndWinControl();
      handleHoldAndWinManualStop = holdAndWinControl.handleManualStop;
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const {
        useHoldAndWinCollectionController,
      } = require("../../../../Hooks/useHoldAndWinCollectionController");
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const collectionController = useHoldAndWinCollectionController();
      triggerCollectionSkip = collectionController.triggerCollectionSkip;
    } catch (e) {
      // Context provider not available - features won't be used
    }
  }

  // Pot Collection hooks - conditionally imported like H&W
  let handlePotCollectionManualStop = null;
  let requestPotCollectionSlamStop = null;
  if (FEATURES_CONFIG.POT_COLLECTION?.ENABLED) {
    try {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const {
        usePotCollectionControl,
      } = require("../../../../Contexts/PotCollectionControlContext");
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const pcControl = usePotCollectionControl();
      handlePotCollectionManualStop = pcControl.handleManualStop;
      requestPotCollectionSlamStop = pcControl.requestSlamStop;
    } catch (e) {
      // Context provider not available - features won't be used
    }
  }

  // Get current speed and game state
  const autoPlaySpeed = useSelector(selectAutoPlaySpeed);
  const pendingAutoPlaySpeed = useSelector(selectPendingAutoPlaySpeed);
  const isSpinning = useSelector(selectIsSpinning);
  const windupComplete = useSelector(selectWindupComplete);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const featureSpinActive = useSelector(selectFeatureSpinActive);
  const showHoldAndWinCollector = useSelector(selectShowHoldAndWinCollector);
  const potCollectionActive = useSelector(selectPotCollectionActive);
  const anyFeatureTriggeredOrActive = useSelector(
    selectAnyFeatureTriggeredOrActive,
  );
  const replaySessionActive = useSelector(selectReplaySessionActive);
  // ── The speed button inside the bonus rooms (2026-10-23: the client wants
  // turbo in free spins and Hold & Win) ── live once the room is on screen
  // (free spins, or the Cleopatra H&W slice); the intros and the wipes in
  // between stay locked — the wipe in brings the speed back to auto.
  const inBonusRoom = useSelector(
    (state) => !!state.freeSpins?.active || !!state.holdAndWin?.active,
  );
  // The H&W room's respins (null outside its provider).
  const respinControl = useRespinControl();
  // The free-spins round (null outside its provider).
  const freeSpinControl = useFreeSpinBasicControl();
  const speedLocked =
    (anyFeatureTriggeredOrActive && !inBonusRoom) || replaySessionActive;

  // Shared hover/press hook
  const {
    buttonRef,
    handlePointerOver,
    handlePointerOut,
    triggerPress,
    cleanupFilters,
    alpha,
    cursor,
  // } = useButtonBrightness({ disabled: anyFeatureTriggeredOrActive || replaySessionActive });
  } = useButtonBrightness({ disabled: speedLocked });

  // Determine current effective speed (pending takes precedence if set)
  const currentSpeed = pendingAutoPlaySpeed || autoPlaySpeed;

  // Add ref to track if button is currently processing a click (locked for duration of spin)
  const isProcessingClickRef = useRef(false);

  // Track if manual stop has been called for the current spin
  const manualStopCalledThisSpinRef = useRef(false);

  // Track pending speed change to apply after spin completes
  const pendingSpeedChangeRef = useRef(null);

  // Track visual state for button appearance (separate from actual speed state)
  const visuallyActiveTurboRef = useRef(false);

  // CRITICAL: Action gating to prevent rapid clicks and respect windup phase
  // Controls which action type is currently allowed: 'TURBO_TOGGLE', or null (no action)
  const allowedActionRef = useRef(null);

  // CRITICAL: Pending stop mechanism - allows user to queue a turbo toggle during windup
  // User can click turbo immediately after spin starts, but stop waits for windup to complete
  const pendingStopRef = useRef(false);

  // Determine the texture for the current speed state
  const getSpeedTextureName = (speed) => {
    if (isSuperTurbo(speed)) return "icon_super_turbo";
    if (isTurboLike(speed)) return "icon_turbo";
    return "icon_turbo_inactive";
  };

  const texture = getNewUITexture(getSpeedTextureName(currentSpeed));

  // Get responsive configuration from context - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(TURBO_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Calculate responsive button position based on device and orientation - memoized
  // Uses toolbar width instead of full screen width
  const buttonPosition = useMemo(() => {
    if (isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2 + buttonConfig.width * (buttonConfig.sideOffset ?? 1.875),
          y:
            -toolbar.visualHeight -
            buttonConfig.height -
            buttonConfig.height * (buttonConfig.gapAboveToolbar ?? 0.125),
        };
      } else {
        return {
          x: toolbar.width - buttonConfig.width * 0.6,
          y: -dimensions.height / 2 + buttonConfig.height * 4.075,
        };
      }
    } else if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2 + buttonConfig.width * 1.8,
          y:
            -toolbar.visualHeight -
            buttonConfig.height -
            buttonConfig.height * 0.25,
        };
      } else {
        // landscape
        return {
          x: toolbar.width - buttonConfig.width * 0.3,
          y: -dimensions.height / 2 + buttonConfig.height * 4.075,
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2 + buttonConfig.width * 1.85,
          y: -dimensions.height / 5.1,
        };
      } else {
        return {
          x: toolbar.width - buttonConfig.width * 0.9,
          y: -buttonConfig.height * 1.65,
        };
      }
    } else {
      return {
        x: toolbar.width - buttonConfig.width,
        y: toolbar.height / 2 - buttonConfig.height * 0.2,
      };
    }
  }, [
    buttonConfig.gapAboveToolbar,
    buttonConfig.sideOffset,
    device,
    orientation,
    isMobileSmall,
    dimensions.height,
    toolbar.width,
    toolbar.height,
    toolbar.visualHeight,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  const handleClick = () => {
    // EVENT-LEVEL THROTTLE: FIRST LINE - Instant return if throttle is active
    // Prevents performance degradation from rapid clicking (50+ clicks/sec)
    if (clickThrottleActive) {
      return; // Instant exit - zero overhead, no logs, no code execution
    }

    // Set throttle flag IMMEDIATELY and schedule single reset
    clickThrottleActive = true;
    if (clickThrottleTimeout) {
      clearTimeout(clickThrottleTimeout);
    }
    clickThrottleTimeout = setTimeout(() => {
      clickThrottleActive = false;
      clickThrottleTimeout = null;
    }, MIN_CLICK_GAP);

    // Block interaction when button is disabled (during features/bonus/replay)
    // if (anyFeatureTriggeredOrActive || replaySessionActive) {
    //   return;
    // }
    if (speedLocked) {
      return;
    }

    // ALWAYS provide visual feedback first (before any checks)
    soundManager.play(SOUND_NAME.button_click);
    triggerPress({
      targetWidth: buttonConfig.width,
      targetHeight: buttonConfig.height,
    });

    // Rate limiting - prevent rapid spam clicks (secondary check)
    const now = Date.now();
    if (now - lastClickTime < MIN_CLICK_GAP) {
      return; // Visual feedback played, but action blocked
    }
    lastClickTime = now;

    // Close menu if open
    dispatch(closeMenu());
    dispatch(closeSymbolPopup());

    // Attempt to toggle fullscreen if conditions are met
    attemptFullscreenToggle(dispatch, device);

    // ── In a bonus room: the speed changes, nothing else (2026-10-23) ──
    // auto → turbo → super turbo → auto, as in the base game. The rooms read
    // it as each spin / respin leaves, so the press applies from the next one
    // (the spin in the air keeps its own). NoSave: the wipe out restores the
    // base game's own speed, the wipe in sets auto.
    if (inBonusRoom) {
      let roomSpeed;
      if (!FEATURES_CONFIG.SUPER_TURBO.ENABLED) {
        roomSpeed = isTurboLike(currentSpeed) ? "auto" : "turbo";
      } else if (currentSpeed === "auto" || currentSpeed === "quick") {
        roomSpeed = "turbo";
      } else if (currentSpeed === "turbo") {
        roomSpeed = "super_turbo";
      } else {
        roomSpeed = "auto";
      }
      // ── Into turbo mid-respin: that respin lands NOW (2026-10-23) ── the
      // base game's rule — the round in the air is stopped, the next takes the
      // new speed. Only from auto (between turbo and super turbo nothing is
      // stopped, as in the base game); the room guards the rest (cells turning,
      // once per respin).
      // if (
      //   store.getState().holdAndWin?.active &&
      //   isTurboLike(roomSpeed) &&
      //   !isTurboLike(currentSpeed)
      // ) {
      //   respinControl?.requestTurboStop?.();
      // }
      // ...and the free spins the same (2026-10-23: "identical as autoplay in
      // base game"): their own manual stop, which keeps the base game's
      // guards — nothing turning = nothing, ONE stop per spin, a reel at a time
      // in the anticipation.
      if (isTurboLike(roomSpeed) && !isTurboLike(currentSpeed)) {
        if (store.getState().holdAndWin?.active) {
          respinControl?.requestTurboStop?.();
        } else if (store.getState().freeSpins?.active) {
          freeSpinControl?.handleManualStop?.();
        }
      }
      dispatch(setAutoplaySpeedNoSave(roomSpeed));
      visuallyActiveTurboRef.current = isTurboLike(roomSpeed);
      if (buttonRef.current) {
        buttonRef.current.texture = getNewUITexture(getSpeedTextureName(roomSpeed));
      }
      return;
    }

    // If collector is showing, enable turbo mode and skip collection animation
    if (showHoldAndWinCollector) {
      // Enable turbo mode if not already active
      if (!isTurboLike(currentSpeed)) {
        dispatch(setAutoplaySpeed("turbo"));
        visuallyActiveTurboRef.current = true;

        // Update texture to show active state
        if (buttonRef.current) {
          const newTexture = getNewUITexture("icon_turbo");
          buttonRef.current.texture = newTexture;
        }
      }

      // Trigger faster collection
      if (triggerCollectionSkip) triggerCollectionSkip();
      return;
    }

    // Action gating - handle different phases with pending stop support
    if (allowedActionRef.current === "TURBO_TOGGLE") {
      // Allowed, proceed normally
    } else if (
      allowedActionRef.current === null &&
      (isSpinning || featureSpinActive) &&
      !windupComplete
    ) {
      // During windup, queue the turbo toggle (only once)
      if (!pendingStopRef.current) {
        pendingStopRef.current = true;
      }
      return; // Don't execute yet, will execute in windup complete effect
    } else {
      // Not allowed at all
      return;
    }

    // If spinning, only allow ONE action per spin - lock button completely after first click
    if ((isSpinning || featureSpinActive) && isProcessingClickRef.current) {
      return;
    }

    // Cycle through 3 speed modes: auto → turbo → super_turbo → auto
    let newSpeed;
    if (!FEATURES_CONFIG.SUPER_TURBO.ENABLED) {
      // 2-state toggle when super turbo is disabled
      newSpeed = isTurboLike(currentSpeed) ? "auto" : "turbo";
    } else if (currentSpeed === "auto" || currentSpeed === "quick") {
      newSpeed = "turbo";
    } else if (currentSpeed === "turbo") {
      newSpeed = "super_turbo";
    } else {
      // super_turbo → auto
      newSpeed = "auto";
    }

    // If spinning, trigger manual stop ONCE per spin (like ButtonPlay)
    if (isSpinning || featureSpinActive) {
      // Lock action gate to prevent any more actions until spin completes
      allowedActionRef.current = null;

      // Lock button for the entire duration of this spin (prevents multiple actions per spin)
      isProcessingClickRef.current = true;

      // Trigger manual stop once per spin when switching TO turbo-like from a non-turbo speed.
      // SKIP manual stop when transitioning BETWEEN turbo-like speeds (turbo↔super_turbo)
      // because turbo modes already stop all reels simultaneously — calling handleManualStop
      // is redundant and disrupts the autoplay lifecycle (causes freeze).
      const alreadyTurboLike = isTurboLike(currentSpeed);
      if (
        isTurboLike(newSpeed) &&
        !alreadyTurboLike &&
        !manualStopCalledThisSpinRef.current
      ) {
        if (potCollectionActive && featureSpinActive) {
          // PC: ALWAYS call both — manual stop for spinning reels, slam stop
          // for feature animations (current or upcoming in this spin cycle).
          handlePotCollectionManualStop && handlePotCollectionManualStop();
          requestPotCollectionSlamStop && requestPotCollectionSlamStop();
          manualStopCalledThisSpinRef.current = true;
        } else if (holdAndWinActive && featureSpinActive) {
          handleHoldAndWinManualStop && handleHoldAndWinManualStop();
          manualStopCalledThisSpinRef.current = true;
        } else if (isSpinning) {
          handleManualStop();
          manualStopCalledThisSpinRef.current = true;
        }
      }

      // Store pending speed change in ref (will be applied after spin completes)
      // DO NOT dispatch during active spin - it causes re-renders that corrupt reel state
      pendingSpeedChangeRef.current = newSpeed;

      // For PC features: also push the pending speed into Redux immediately so
      // FeatureExecutor._isFastMode() can see it. PC reels have already stopped,
      // so the re-render concern doesn't apply here.
      if (potCollectionActive && featureSpinActive && isTurboLike(newSpeed)) {
        dispatch(setPendingAutoplaySpeed(newSpeed));
      }

      // Update visual state immediately for instant feedback
      visuallyActiveTurboRef.current = isTurboLike(newSpeed);

      // Force texture update by directly changing the sprite texture
      if (buttonRef.current) {
        buttonRef.current.texture = getNewUITexture(
          getSpeedTextureName(newSpeed),
        );
      }
    } else {
      // Not spinning - check if autoplay is active
      const state = store.getState();

      if (state.settings.autoPlayActive) {
        // During autoplay, ALWAYS use pending speed (even when not spinning)
        // This prevents race condition with autoplay effect triggering next spin
        // NOTE: Do NOT set pendingSpeedChangeRef here — the speed is dispatched
        // directly via Redux. The ref is only for deferring during active spins.
        // Setting it here would leak across spins because the reset effect
        // (deps: [isSpinning]) won't re-fire when we're already not spinning.
        dispatch(setPendingAutoplaySpeed(newSpeed));
      } else {
        // Manual mode - apply immediately
        dispatch(setAutoplaySpeed(newSpeed));
      }

      // Update visual state
      visuallyActiveTurboRef.current = isTurboLike(newSpeed);
    }
  };

  // Initialize action gate on mount
  useEffect(() => {
    // On mount, if not spinning, allow turbo toggle
    if (!isSpinning) {
      allowedActionRef.current = "TURBO_TOGGLE";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount - isSpinning checked but not in deps intentionally

  // CRITICAL: Action gate management - controls which action is allowed at each phase
  // This prevents rapid clicks by blocking actions that don't match the current game phase
  useEffect(() => {
    // When windup completes and we're spinning, allow TURBO_TOGGLE action (can trigger stop)
    if (isSpinning && windupComplete) {
      allowedActionRef.current = "TURBO_TOGGLE";

      // Check if there's a pending turbo toggle and execute it
      if (pendingStopRef.current) {
        pendingStopRef.current = false;
        allowedActionRef.current = null; // Lock immediately after executing

        // Execute the queued turbo toggle with manual stop
        let newSpeed;
        if (!FEATURES_CONFIG.SUPER_TURBO.ENABLED) {
          newSpeed = isTurboLike(currentSpeed) ? "auto" : "turbo";
        } else if (currentSpeed === "auto" || currentSpeed === "quick") {
          newSpeed = "turbo";
        } else if (currentSpeed === "turbo") {
          newSpeed = "super_turbo";
        } else {
          newSpeed = "auto";
        }
        isProcessingClickRef.current = true;

        if (isTurboLike(newSpeed) && !manualStopCalledThisSpinRef.current) {
          if (potCollectionActive && featureSpinActive) {
            handlePotCollectionManualStop && handlePotCollectionManualStop();
            requestPotCollectionSlamStop && requestPotCollectionSlamStop();
            manualStopCalledThisSpinRef.current = true;
          } else if (holdAndWinActive && featureSpinActive) {
            handleHoldAndWinManualStop && handleHoldAndWinManualStop();
            manualStopCalledThisSpinRef.current = true;
          } else if (isSpinning) {
            handleManualStop();
            manualStopCalledThisSpinRef.current = true;
          }
        }

        // Store pending speed change
        pendingSpeedChangeRef.current = newSpeed;
        visuallyActiveTurboRef.current = isTurboLike(newSpeed);

        // For PC features: push pending speed into Redux immediately
        if (potCollectionActive && featureSpinActive && isTurboLike(newSpeed)) {
          dispatch(setPendingAutoplaySpeed(newSpeed));
        }

        // Update texture
        if (buttonRef.current) {
          buttonRef.current.texture = getNewUITexture(
            getSpeedTextureName(newSpeed),
          );
        }
      }
    }
  }, [
    isSpinning,
    windupComplete,
    currentSpeed,
    holdAndWinActive,
    potCollectionActive,
    featureSpinActive,
    handleManualStop,
    handleHoldAndWinManualStop,
    handlePotCollectionManualStop,
    requestPotCollectionSlamStop,
    buttonRef,
    dispatch,
  ]);

  // When spin completes, allow TURBO_TOGGLE action for next spin
  useEffect(() => {
    if (!isSpinning && !featureSpinActive) {
      allowedActionRef.current = "TURBO_TOGGLE";
      pendingStopRef.current = false; // Clear any pending turbo toggle
    }
  }, [isSpinning, featureSpinActive]);

  // Sync visual state with actual Redux state
  useEffect(() => {
    const effectiveSpeed = pendingAutoPlaySpeed || currentSpeed;

    // Update texture to match actual state
    if (buttonRef.current) {
      buttonRef.current.texture = getNewUITexture(
        getSpeedTextureName(effectiveSpeed),
      );
    }
  }, [currentSpeed, pendingAutoPlaySpeed, buttonRef]);

  // Reset flags when spin ends - unlock button for next spin
  useEffect(() => {
    if (!isSpinning && !featureSpinActive) {
      // Apply pending speed change if one was queued during the spin
      if (pendingSpeedChangeRef.current !== null) {
        dispatch(setPendingAutoplaySpeed(pendingSpeedChangeRef.current));
        pendingSpeedChangeRef.current = null;
      }

      // Reset all flags and action gate for next spin
      manualStopCalledThisSpinRef.current = false;
      isProcessingClickRef.current = false;
      pendingStopRef.current = false; // Clear any pending turbo toggle
      allowedActionRef.current = "TURBO_TOGGLE";
    }
  }, [isSpinning, featureSpinActive, dispatch]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanupFilters();
      // Cleanup throttle timeout
      if (clickThrottleTimeout) {
        clearTimeout(clickThrottleTimeout);
        clickThrottleTimeout = null;
        clickThrottleActive = false;
      }
    };
  }, [cleanupFilters]);

  // Calculate button radius to match the visual sprite exactly - memoized
  const buttonRadius = useMemo(
    () => buttonConfig.width / 2,
    [buttonConfig.width],
  );

  return (
    <Container
      name="secondary_button_turbo"
      x={buttonPosition.x}
      y={buttonPosition.y}
    >
      {/* Interactive circular area - handles all pointer events */}
      <Graphics
        name="button_turbo_interactive_area"
        eventMode="static"
        cursor={cursor}
        hitArea={new PIXI.Circle(0, 0, buttonRadius)}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        draw={(g) => {
          g.clear();
          // Nearly invisible fill (required for hit detection to work)
          g.beginFill(0x000000, 0.01);
          g.drawCircle(0, 0, buttonRadius);
          g.endFill();
        }}
      />

      {/* Visual sprite (non-interactive, just for display) */}
      {/* Soft drop shadow under it (ButtonShadow, utils/buttonShadow) */}
      <ButtonShadow target={buttonRef} texture={texture} width={buttonConfig.width} height={buttonConfig.height} />
      <Sprite
        ref={buttonRef}
        texture={texture}
        name="button_turbo_icon"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        x={0}
        y={0}
        eventMode="none"
        alpha={alpha}
        roundPixels={true}
      />
    </Container>
  );
};

export default ButtonTurbo;
